<template>
  <!--
    เครื่องราชอิสริยาภรณ์ (ส่วนกลาง) — กำกับติดตามการรายงานของสถานศึกษา ตรวจสอบประวัติ และรวมรายชื่อเสนอขอ
    API: decoration_admin.php (ผลตรวจคุณสมบัติใช้ decoration_helpers.php ตัวเดียวกับหน้าสถานศึกษา /college/decoration)
  -->
  <v-container fluid class="text_google">
    <base-material-card icon="mdi-medal" title="เครื่องราชอิสริยาภรณ์ — กำกับติดตามการรายงานและตรวจสอบประวัติ" class="px-5 py-3">
      <v-row dense align="center" class="mb-2">
        <v-col cols="12" md="3">
          <v-select v-model="year" :items="yearItems" label="ปีที่ขอพระราชทาน (พ.ศ.)" outlined dense hide-details @change="loadMonitor" />
        </v-col>
        <v-col cols="12" md="9" class="text-md-right">
          <span v-if="data.period" class="caption mr-3">
            กำหนดส่งของสถานศึกษา {{ thaiDate(data.period.start) }} – {{ thaiDate(data.period.stop) }}
            <v-chip x-small :color="data.period.enabled ? 'success' : 'grey'" dark>{{ data.period.enabled ? "เปิด" : "ปิด" }}</v-chip>
          </span>
          <span v-else class="caption mr-3 orange--text">ยังไม่ตั้งกำหนดการ (period_college type decoration ปี {{ year }})</span>
          <v-btn small outlined color="primary" to="/admin/period_calendar" class="mr-1">
            <v-icon small left>mdi-calendar-edit</v-icon>ตั้งกำหนดการ
          </v-btn>
          <v-btn small outlined :loading="loading" @click="loadMonitor"><v-icon small left>mdi-refresh</v-icon>คำนวณใหม่</v-btn>
        </v-col>
      </v-row>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />
      <div v-if="loading" class="caption grey--text mb-2">กำลังตรวจคุณสมบัติบุคลากรทั้งประเทศ (ประมาณ 3–5 วินาที)…</div>

      <template v-if="data.totals">
        <v-row dense class="mb-3">
          <v-col v-for="k in kpis" :key="k.label" cols="6" md="3" lg="">
            <div class="kpi-box">
              <div class="caption grey--text">{{ k.label }}</div>
              <div class="kpi-value" :class="k.cls">{{ k.value }}</div>
              <div class="caption">{{ k.hint }}</div>
            </div>
          </v-col>
        </v-row>

        <v-tabs v-model="tab" grow class="mb-2" @change="onTab">
          <v-tab><v-icon left>mdi-radar</v-icon>ติดตามการรายงาน ({{ data.units.length }})</v-tab>
          <v-tab><v-icon left>mdi-shield-search</v-icon>ตรวจสอบประวัติ{{ audit ? ` (${audit.issues.length})` : "" }}</v-tab>
          <v-tab><v-icon left>mdi-format-list-numbered</v-icon>รายชื่อเสนอขอรวม ({{ data.totals.requests }})</v-tab>
          <v-tab><v-icon left>mdi-cash</v-icon>เกณฑ์เงินเดือน</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <!-- ===== ติดตามการรายงาน ===== -->
          <v-tab-item>
            <v-row dense class="my-2">
              <v-col cols="12" md="3">
                <v-text-field v-model="unitSearch" prepend-inner-icon="mdi-magnify" label="ค้นหาหน่วยงาน/จังหวัด" outlined dense hide-details clearable />
              </v-col>
              <v-col cols="6" md="2">
                <v-select v-model="regionFilter" :items="regionItems" label="ภาค" outlined dense hide-details clearable />
              </v-col>
              <v-col cols="6" md="3">
                <v-select v-model="stateFilter" :items="stateItems" label="สถานะการรายงาน" outlined dense hide-details clearable />
              </v-col>
              <v-col cols="6" md="2">
                <v-select v-model="coverageFilter" :items="coverageItems" label="บันทึกประวัติ" outlined dense hide-details clearable />
              </v-col>
              <v-col cols="6" md="2" class="text-right">
                <v-btn outlined color="success" @click="exportUnits"><v-icon left>mdi-microsoft-excel</v-icon>Excel</v-btn>
              </v-col>
            </v-row>
            <v-data-table :headers="unitHeaders" :items="filteredUnits" :search="unitSearch" item-key="college_code" dense
              :items-per-page="50" :footer-props="{ 'items-per-page-options': [50, 100, -1] }" sort-by="coverage" class="dec-table">
              <template v-slot:[`item.college_name`]="{ item }">
                <div class="font-weight-medium">{{ item.college_name }}</div>
                <div class="caption grey--text">{{ item.college_code }} · {{ item.province_name || "-" }} · {{ item.region_name || "-" }}</div>
              </template>
              <template v-slot:[`item.coverage`]="{ item }">
                <div style="min-width:120px">
                  <v-progress-linear :value="item.coverage || 0" height="14" rounded :color="coverageColor(item.coverage)">
                    <span class="caption white--text font-weight-bold">{{ item.coverage === null ? "-" : item.coverage + "%" }}</span>
                  </v-progress-linear>
                  <div class="caption grey--text">บันทึกแล้ว {{ item.recorded }} · ยังไม่บันทึก {{ item.no_record }}</div>
                  <div v-if="item.unreadable" class="caption error--text">อ่านชั้นตราไม่ได้ {{ item.unreadable }} รายการ</div>
                </div>
              </template>
              <template v-slot:[`item.eligible`]="{ item }">
                <span class="success--text font-weight-bold">{{ item.eligible }}</span>
                <span class="caption"> / ตรวจสอบ {{ item.review }}</span>
                <div class="caption grey--text">ข้อมูลไม่พอ {{ item.need_data }} · ยังไม่ครบ {{ item.not_yet }}</div>
              </template>
              <template v-slot:[`item.state`]="{ item }">
                <v-chip small :color="STATE[item.state].color" dark>{{ STATE[item.state].label }}</v-chip>
                <div v-if="item.submitted_at" class="caption">{{ thaiDate(item.submitted_at) }}{{ item.doc_no ? " · " + item.doc_no : "" }}</div>
                <div v-if="item.state === 'returned'" class="caption error--text reason">{{ item.returned_note }}</div>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex" style="gap:4px">
                  <v-btn x-small outlined color="primary" @click="openUnit(item)">รายละเอียด</v-btn>
                  <v-btn v-if="item.state === 'submitted'" x-small depressed color="success" :disabled="!data.can_edit"
                    @click="review(item, 'accepted')">ตรวจรับ</v-btn>
                  <v-btn v-if="['submitted', 'accepted'].includes(item.state)" x-small outlined color="error" :disabled="!data.can_edit"
                    @click="openReturn(item)">ส่งกลับ</v-btn>
                </div>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- ===== ตรวจสอบประวัติ ===== -->
          <v-tab-item>
            <v-progress-linear v-if="auditLoading" indeterminate class="my-2" />
            <template v-if="audit">
              <v-alert type="info" dense text class="mt-3 caption">
                ประวัติเครื่องราชฯ ทั้งหมด {{ fmt(audit.records) }} รายการ ของ {{ fmt(audit.people) }} คน — ตรวจรูปแบบชั้นตรา วันที่ ลำดับชั้นตรา
                และระยะห่างตามข้อ ๑๑ · กดชื่อเพื่อเปิดประวัติและแก้ไข (Admin แก้ไขได้ทุกหน่วยงาน)
              </v-alert>
              <div class="d-flex flex-wrap mb-2" style="gap:6px">
                <v-chip v-for="(t, key) in audit.audit_types" :key="key" small label :outlined="auditType !== key"
                  :color="LEVEL[t[0]].color" :dark="auditType === key" @click="auditType = auditType === key ? null : key">
                  {{ t[1] }} <b class="ml-1">{{ auditCount[key] || 0 }}</b>
                </v-chip>
              </div>
              <v-row dense class="mb-1">
                <v-col cols="12" md="5">
                  <v-text-field v-model="auditSearch" prepend-inner-icon="mdi-magnify" label="ค้นหาชื่อ/หน่วยงาน/เลขบัตร" outlined dense hide-details clearable />
                </v-col>
                <v-col cols="12" md="7" class="text-right">
                  <v-btn outlined color="success" @click="exportAudit"><v-icon left>mdi-microsoft-excel</v-icon>Excel</v-btn>
                </v-col>
              </v-row>
              <v-data-table :headers="auditHeaders" :items="filteredIssues" :search="auditSearch" dense :items-per-page="50"
                :footer-props="{ 'items-per-page-options': [50, 100, -1] }" no-data-text="ไม่พบปัญหา">
                <template v-slot:[`item.level`]="{ item }">
                  <v-chip x-small :color="LEVEL[item.level].color" dark>{{ LEVEL[item.level].label }}</v-chip>
                </template>
                <template v-slot:[`item.full_name`]="{ item }">
                  <a @click="openHistory(item)">{{ item.full_name }}</a>
                  <div class="caption grey--text">{{ item.id_card }}</div>
                </template>
                <template v-slot:[`item.label`]="{ item }">
                  <div class="caption">{{ item.label }}</div>
                  <div class="caption font-weight-bold">{{ item.detail }}</div>
                </template>
              </v-data-table>
            </template>
          </v-tab-item>

          <!-- ===== รายชื่อเสนอขอรวม ===== -->
          <v-tab-item>
            <v-progress-linear v-if="reqLoading" indeterminate class="my-2" />
            <template v-if="requests">
              <div class="subtitle-2 font-weight-bold mt-3">จำนวนชั้นตราที่เสนอขอ จำแนกประเภทบุคลากร
                <span class="caption grey--text">(เฉพาะหน่วยงานที่ตรวจรับแล้ว / ทั้งหมด)</span></div>
              <v-simple-table dense class="mb-3">
                <thead>
                  <tr><th>ชั้นตรา</th><th v-for="t in reqTypes" :key="t" class="text-center">{{ typeLabel(t) }}</th><th class="text-center">รวม</th></tr>
                </thead>
                <tbody>
                  <tr v-for="c in reqClasses" :key="c.abbr">
                    <td>{{ c.name }} ({{ c.abbr }})</td>
                    <td v-for="t in reqTypes" :key="t" class="text-center">{{ countReq(c.abbr, t, true) }} / {{ countReq(c.abbr, t) }}</td>
                    <td class="text-center font-weight-bold">{{ countReq(c.abbr, null, true) }} / {{ countReq(c.abbr) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div class="d-flex align-center mb-2">
                <v-switch v-model="acceptedOnly" dense hide-details label="เฉพาะหน่วยงานที่ตรวจรับแล้ว" class="mt-0" />
                <v-spacer />
                <v-btn outlined color="success" @click="exportRequests"><v-icon left>mdi-microsoft-excel</v-icon>Excel รายชื่อ</v-btn>
              </div>
              <v-data-table :headers="reqHeaders" :items="filteredRequests" dense :items-per-page="50"
                :footer-props="{ 'items-per-page-options': [50, 100, -1] }" no-data-text="ยังไม่มีรายชื่อ">
                <template v-slot:[`item.name`]="{ item }">
                  {{ item.snapshot.full_name }}<div class="caption grey--text">{{ item.snapshot.position_name }}</div>
                </template>
                <template v-slot:[`item.person_type`]="{ item }">{{ typeLabel(item.person_type) }}</template>
                <template v-slot:[`item.request_class`]="{ item }"><b>{{ item.request_class }}</b></template>
                <template v-slot:[`item.unit_state`]="{ item }">
                  <v-chip x-small :color="STATE[unitState(item.college_code)].color" dark>{{ STATE[unitState(item.college_code)].label }}</v-chip>
                  <v-chip v-if="item.eligible_status === 'review'" x-small color="amber darken-2" dark class="ml-1">ต้องตรวจสอบ</v-chip>
                </template>
              </v-data-table>
            </template>
          </v-tab-item>

          <!-- ===== เกณฑ์เงินเดือน ===== -->
          <v-tab-item>
            <v-alert type="warning" dense text class="mt-3 caption">
              ใช้ตรวจเงื่อนไขเงินเดือนตามบัญชีท้ายระเบียบ — ตรวจกับบัญชีอัตราเงินเดือนที่ใช้บังคับปัจจุบันก่อนเปิดให้สถานศึกษาเสนอรายชื่อ
              {{ data.can_edit ? "" : " (แก้ไขได้เฉพาะ SuperAdmin/ภารกิจข้อมูลพื้นฐาน)" }}
            </v-alert>
            <v-simple-table dense>
              <tbody>
                <tr v-for="(s, key) in data.settings" :key="key">
                  <td class="caption">{{ s.label }} <span class="grey--text">[{{ key }}]</span></td>
                  <td style="width:240px">
                    <v-text-field v-model.number="settingEdit[key]" type="number" dense hide-details suffix="บาท" :disabled="!data.can_edit"
                      append-outer-icon="mdi-content-save" @click:append-outer="saveSetting(key)" />
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-tab-item>
        </v-tabs-items>
      </template>
    </base-material-card>

    <!-- รายละเอียดหน่วยงาน -->
    <v-dialog v-model="unitDialog" max-width="1250" scrollable>
      <v-card v-if="unit">
        <v-toolbar dense dark color="primary" flat class="flex-grow-0">
          <v-toolbar-title class="subtitle-1">{{ unit.college_name }} ({{ unit.college_code }}) — ปี {{ year }}</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="unitDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-3">
          <v-progress-linear v-if="unitLoading" indeterminate />
          <template v-if="unitData">
            <div class="d-flex flex-wrap mb-2" style="gap:6px">
              <v-chip small label>บุคลากร {{ unitData.stats.persons }}</v-chip>
              <v-chip small label :color="coverageColor(unitData.stats.coverage)" dark>บันทึกประวัติ {{ unitData.stats.coverage === null ? "-" : unitData.stats.coverage }}%</v-chip>
              <v-chip v-for="k in ['eligible', 'review', 'not_yet', 'need_data', 'max_reached', 'not_covered']" :key="k" small label
                :color="STATUS[k].color" outlined>{{ STATUS[k].label }} {{ unitData.stats[k] }}</v-chip>
              <v-chip small label color="primary">เสนอขอ {{ unitData.requests.length }}</v-chip>
              <v-chip small label :color="STATE[unitStateOf(unitData.submission)].color" dark>{{ STATE[unitStateOf(unitData.submission)].label }}</v-chip>
            </div>
            <v-tabs v-model="unitTab" dense>
              <v-tab>รายชื่อที่เสนอขอ ({{ unitData.requests.length }})</v-tab>
              <v-tab>บุคลากรและผลตรวจ ({{ unitData.persons.length }})</v-tab>
              <v-tab>ปัญหาประวัติ ({{ unitData.issues.length }})</v-tab>
            </v-tabs>
            <v-tabs-items v-model="unitTab">
              <v-tab-item>
                <v-data-table :headers="unitReqHeaders" :items="unitData.requests" dense hide-default-footer :items-per-page="-1"
                  no-data-text="ยังไม่มีรายชื่อ">
                  <template v-slot:[`item.name`]="{ item }">
                    <a @click="openHistory({ id_card: item.id_card, full_name: item.snapshot.full_name })">{{ item.snapshot.full_name }}</a>
                    <div class="caption grey--text">{{ item.snapshot.position_name }} · {{ typeLabel(item.person_type) }}</div>
                  </template>
                  <template v-slot:[`item.request_class`]="{ item }"><b>{{ item.request_class }}</b></template>
                  <template v-slot:[`item.last_class`]="{ item }">{{ item.last_class || "-" }} <span class="caption">{{ thaiDate(item.last_class_date) }}</span></template>
                  <template v-slot:[`item.certify`]="{ item }">
                    <v-chip x-small :color="certifyComplete(item.certify) ? 'success' : 'warning'" dark>
                      {{ CERTIFY_ITEMS.filter(i => item.certify[i.key]).length }}/{{ CERTIFY_ITEMS.length }}
                    </v-chip>
                    <v-chip v-if="item.eligible_status === 'review'" x-small color="amber darken-2" dark>ต้องตรวจสอบ</v-chip>
                  </template>
                  <template v-slot:[`item.note`]="{ item }"><span class="caption">{{ item.note || "-" }}</span></template>
                </v-data-table>
              </v-tab-item>
              <v-tab-item>
                <v-data-table :headers="unitPersonHeaders" :items="unitData.persons" dense :items-per-page="25" item-key="id_card">
                  <template v-slot:[`item.full_name`]="{ item }">
                    <a @click="openHistory(item)">{{ item.full_name }}</a>
                    <div class="caption grey--text">{{ item.position_name }} {{ item.rang }}</div>
                  </template>
                  <template v-slot:[`item.highest`]="{ item }">
                    {{ item.eval.highest ? item.eval.highest.class + " " + thaiDate(item.eval.highest.date) : (item.never_received ? "ไม่เคยได้รับ" : "-") }}
                  </template>
                  <template v-slot:[`item.status`]="{ item }">
                    <v-chip x-small :color="STATUS[item.eval.status].color" dark>{{ STATUS[item.eval.status].label }}</v-chip>
                    <b class="ml-1">{{ item.eval.next_class }}</b>
                    <div class="caption grey--text reason">{{ item.eval.reason }}</div>
                  </template>
                </v-data-table>
              </v-tab-item>
              <v-tab-item>
                <v-data-table :headers="auditHeaders.filter(h => h.value !== 'college_name')" :items="unitData.issues" dense
                  hide-default-footer :items-per-page="-1" no-data-text="ไม่พบปัญหา">
                  <template v-slot:[`item.level`]="{ item }">
                    <v-chip x-small :color="LEVEL[item.level].color" dark>{{ LEVEL[item.level].label }}</v-chip>
                  </template>
                  <template v-slot:[`item.full_name`]="{ item }"><a @click="openHistory(item)">{{ item.full_name }}</a></template>
                  <template v-slot:[`item.label`]="{ item }">
                    <div class="caption">{{ item.label }}</div><div class="caption font-weight-bold">{{ item.detail }}</div>
                  </template>
                </v-data-table>
              </v-tab-item>
            </v-tabs-items>
          </template>
        </v-card-text>
        <v-card-actions v-if="unitData && unitData.submission">
          <v-spacer />
          <v-btn v-if="unitData.submission.status === 'accepted'" text color="warning" :disabled="!data.can_edit" @click="review(unit, 'reopen')">
            ยกเลิกการตรวจรับ
          </v-btn>
          <v-btn v-if="['submitted', 'accepted'].includes(unitData.submission.status)" outlined color="error" :disabled="!data.can_edit"
            @click="openReturn(unit)">ส่งกลับให้แก้ไข</v-btn>
          <v-btn v-if="unitData.submission.status === 'submitted'" color="success" :disabled="!data.can_edit" @click="review(unit, 'accepted')">
            ตรวจรับรายชื่อ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ส่งกลับ -->
    <v-dialog v-model="returnDialog" max-width="520">
      <v-card v-if="returnUnit">
        <v-toolbar dense dark color="error" flat><v-toolbar-title class="subtitle-1">ส่งกลับให้แก้ไข — {{ returnUnit.college_name }}</v-toolbar-title></v-toolbar>
        <v-card-text class="pt-4">
          <v-textarea v-model="returnNote" outlined rows="3" label="เหตุผล/สิ่งที่ต้องแก้ไข (สถานศึกษาเห็นข้อความนี้)" counter="500" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="returnDialog = false">ยกเลิก</v-btn>
          <v-btn color="error" :loading="busy" @click="review(returnUnit, 'returned', returnNote)">ส่งกลับ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ประวัติเครื่องราชฯ รายบุคคล -->
    <v-dialog v-model="historyDialog" max-width="1100" scrollable>
      <v-card v-if="historyPerson">
        <v-toolbar dense dark color="indigo" flat class="flex-grow-0">
          <v-toolbar-title class="subtitle-1">ประวัติเครื่องราชอิสริยาภรณ์ — {{ historyPerson.full_name }} ({{ historyPerson.id_card }})</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="historyDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <Personnel_decoration :key="historyPerson.id_card" compact :editpersonnel_temporary="{ id_card: historyPerson.id_card }"
            @changed="historyChanged = true" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :color="snack.color" top :timeout="6000">{{ snack.text }}</v-snackbar>
  </v-container>
</template>

<script>
import * as XLSX from "xlsx";
import Personnel_decoration from "@/views/dashboard/components/college/personnel_decoration.vue";
import { CERTIFY_ITEMS, CLASSES, STATUS, certifyComplete, className, thaiDate } from "@/utils/decorationClasses";

const API = "decoration_admin.php";
const STATE = {
  not_submitted: { label: "ยังไม่ดำเนินการ", color: "grey" },
  draft: { label: "บันทึกแล้ว ยังไม่ส่ง", color: "amber darken-2" },
  submitted: { label: "ยืนยันส่งแล้ว — รอตรวจรับ", color: "blue" },
  accepted: { label: "ตรวจรับแล้ว", color: "success" },
  returned: { label: "ส่งกลับให้แก้ไข", color: "error" }
};
const LEVEL = {
  error: { label: "ผิดพลาด", color: "error" },
  warning: { label: "ต้องตรวจสอบ", color: "orange darken-2" },
  info: { label: "ข้อมูลประกอบ", color: "blue-grey" }
};

export default {
  name: "DecorationAdmin",
  components: { Personnel_decoration },
  data() {
    return {
      STATE,
      LEVEL,
      STATUS,
      CERTIFY_ITEMS,
      year: null,
      loading: false,
      busy: false,
      tab: 0,
      data: { units: [], settings: {}, years: [] },
      settingEdit: {},
      unitSearch: "",
      regionFilter: null,
      stateFilter: null,
      coverageFilter: null,
      audit: null,
      auditLoading: false,
      auditType: null,
      auditSearch: "",
      requests: null,
      reqLoading: false,
      acceptedOnly: false,
      unitDialog: false,
      unit: null,
      unitData: null,
      unitLoading: false,
      unitTab: 0,
      returnDialog: false,
      returnUnit: null,
      returnNote: "",
      historyDialog: false,
      historyPerson: null,
      historyChanged: false,
      snack: { show: false, text: "", color: "success" },
      unitHeaders: [
        { text: "หน่วยงาน", value: "college_name" },
        { text: "บุคลากร", value: "persons", align: "center", width: 80 },
        { text: "บันทึกประวัติเครื่องราชฯ", value: "coverage", width: 200 },
        { text: "ครบเกณฑ์ / ต้องตรวจสอบ", value: "eligible", width: 190 },
        { text: "เสนอขอ", value: "requests", align: "center", width: 80 },
        { text: "สถานะการรายงาน", value: "state", width: 200 },
        { text: "", value: "actions", sortable: false, width: 220 }
      ],
      auditHeaders: [
        { text: "ระดับ", value: "level", width: 110 },
        { text: "ชื่อ-สกุล", value: "full_name" },
        { text: "หน่วยงาน", value: "college_name" },
        { text: "ปัญหา", value: "label" }
      ],
      reqHeaders: [
        { text: "หน่วยงาน", value: "college_name" },
        { text: "จังหวัด", value: "province_name", width: 120 },
        { text: "ชื่อ-สกุล", value: "name", sortable: false },
        { text: "ประเภท", value: "person_type", width: 170 },
        { text: "ชั้นตรา", value: "request_class", width: 80 },
        { text: "สถานะ", value: "unit_state", sortable: false, width: 220 }
      ],
      unitReqHeaders: [
        { text: "ชื่อ-สกุล", value: "name", sortable: false },
        { text: "ชั้นตราที่ขอ", value: "request_class", width: 100 },
        { text: "ชั้นตราเดิม", value: "last_class", width: 160 },
        { text: "รับรองคุณสมบัติ", value: "certify", sortable: false, width: 170 },
        { text: "หมายเหตุ", value: "note", sortable: false }
      ],
      unitPersonHeaders: [
        { text: "ชื่อ-สกุล", value: "full_name" },
        { text: "เกณฑ์", value: "category_ref", width: 140 },
        { text: "ชั้นสูงสุดที่ได้รับ", value: "highest", sortable: false, width: 160 },
        { text: "ผลตรวจ / ขอครั้งถัดไป", value: "status", sortable: false, width: 320 }
      ]
    };
  },
  computed: {
    yearItems() {
      return (this.data.years || []).map(y => ({ text: `${y} (พระราชพิธี 28 ก.ค. ${y})`, value: y }));
    },
    regionItems() {
      return [...new Set(this.data.units.map(u => u.region_name).filter(Boolean))];
    },
    stateItems() {
      return Object.entries(STATE).map(([value, s]) => ({ value, text: s.label }));
    },
    coverageItems() {
      return [
        { value: "none", text: "ยังไม่บันทึกเลย (0%)" },
        { value: "low", text: "ต่ำกว่า 50%" },
        { value: "mid", text: "50–99%" },
        { value: "full", text: "ครบ 100%" }
      ];
    },
    filteredUnits() {
      return this.data.units.filter(u => {
        if (this.regionFilter && u.region_name !== this.regionFilter) return false;
        if (this.stateFilter && u.state !== this.stateFilter) return false;
        const c = u.coverage || 0;
        if (this.coverageFilter === "none" && c > 0) return false;
        if (this.coverageFilter === "low" && c >= 50) return false;
        if (this.coverageFilter === "mid" && (c < 50 || c >= 100)) return false;
        if (this.coverageFilter === "full" && c < 100) return false;
        return true;
      });
    },
    kpis() {
      const t = this.data.totals;
      const sent = t.submitted + t.accepted;
      return [
        { label: "หน่วยงานยืนยันส่งแล้ว", value: `${sent} / ${t.units}`, hint: `ตรวจรับแล้ว ${t.accepted} · ส่งกลับ ${t.returned} · บันทึกยังไม่ส่ง ${t.draft}` },
        { label: "รอส่วนกลางตรวจรับ", value: t.submitted, hint: "หน่วยงานที่ยืนยันส่งแล้ว", cls: t.submitted ? "blue--text" : "" },
        {
          label: "ความครบถ้วนของประวัติเครื่องราชฯ",
          value: (t.coverage || 0) + "%",
          hint: `บันทึก/ยืนยันแล้ว ${this.fmt(t.recorded)} · ยังไม่บันทึก ${this.fmt(t.no_record)} คน`,
          cls: (t.coverage || 0) < 50 ? "error--text" : ""
        },
        { label: "ครบเกณฑ์ปีนี้", value: this.fmt(t.eligible), hint: `ต้องตรวจสอบ ${this.fmt(t.review)} · ข้อมูลไม่พอ ${this.fmt(t.need_data)}` },
        { label: "รายชื่อเสนอขอรวม", value: this.fmt(t.requests), hint: `ส่ง สลค. ภายใน ${thaiDate(this.data.schedule.soc_deadline)}` }
      ];
    },
    auditCount() {
      const c = {};
      (this.audit ? this.audit.issues : []).forEach(i => (c[i.type] = (c[i.type] || 0) + 1));
      return c;
    },
    filteredIssues() {
      return this.audit ? this.audit.issues.filter(i => !this.auditType || i.type === this.auditType) : [];
    },
    acceptedSet() {
      return new Set(this.data.units.filter(u => u.state === "accepted").map(u => u.college_code));
    },
    filteredRequests() {
      return (this.requests || []).filter(r => !this.acceptedOnly || this.acceptedSet.has(r.college_code));
    },
    reqTypes() {
      return ["teacher", "civil", "govem", "permanent"].filter(t => (this.requests || []).some(r => r.person_type === t));
    },
    reqClasses() {
      return CLASSES.filter(c => (this.requests || []).some(r => r.request_class === c.abbr)).reverse();
    }
  },
  mounted() {
    this.loadMonitor();
  },
  watch: {
    historyDialog(open) {
      if (open || !this.historyChanged) return;
      if (this.audit) this.loadAudit();
      if (this.unitDialog && this.unit) this.openUnit(this.unit);
    }
  },
  methods: {
    thaiDate,
    className,
    certifyComplete,
    fmt(v) {
      return Number(v || 0).toLocaleString("th-TH");
    },
    typeLabel(t) {
      return (this.data.type_labels || {})[t] || t;
    },
    coverageColor(c) {
      if (c === null || c === undefined) return "grey";
      return c >= 100 ? "success" : c >= 50 ? "amber darken-2" : "error";
    },
    unitState(code) {
      const u = this.data.units.find(x => x.college_code === code);
      return u ? u.state : "not_submitted";
    },
    unitStateOf(sub) {
      return sub ? sub.status : "not_submitted";
    },
    notify(text, color = "success") {
      this.snack = { show: true, text, color };
    },
    async api(payload) {
      try {
        const res = await this.$http.post(API, { ApiKey: "HRvec2021", year: this.year, ...payload });
        if (!res.data || res.data.status !== true) throw new Error((res.data && res.data.message) || "ไม่ได้รับข้อมูล");
        return res.data;
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message;
        throw new Error(msg || e.message);
      }
    },
    async loadMonitor() {
      this.loading = true;
      try {
        const d = await this.api({ action: "monitor" });
        this.data = d;
        this.year = d.year;
        this.settingEdit = Object.fromEntries(Object.entries(d.settings || {}).map(([k, s]) => [k, s.value]));
        this.requests = null;
        if (this.tab === 2) this.loadRequests();
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.loading = false;
      }
    },
    onTab(t) {
      if (t === 1 && !this.audit) this.loadAudit();
      if (t === 2 && !this.requests) this.loadRequests();
    },
    async loadAudit() {
      this.auditLoading = true;
      try {
        this.audit = await this.api({ action: "audit" });
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.auditLoading = false;
      }
    },
    async loadRequests() {
      this.reqLoading = true;
      try {
        this.requests = (await this.api({ action: "requests" })).requests;
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.reqLoading = false;
      }
    },
    countReq(cls, type, acceptedOnly) {
      return (this.requests || []).filter(
        r => r.request_class === cls && (!type || r.person_type === type) && (!acceptedOnly || this.acceptedSet.has(r.college_code))
      ).length;
    },
    async openUnit(u) {
      this.unit = u;
      this.unitData = null;
      this.unitDialog = true;
      this.unitLoading = true;
      try {
        this.unitData = await this.api({ action: "college", college_code: u.college_code });
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.unitLoading = false;
      }
    },
    openReturn(u) {
      this.returnUnit = u;
      this.returnNote = "";
      this.returnDialog = true;
    },
    async review(u, status, note) {
      if (status === "accepted" && !confirm(`ตรวจรับรายชื่อของ ${u.college_name}? สถานศึกษาจะแก้ไขรายชื่อไม่ได้`)) return;
      this.busy = true;
      try {
        const d = await this.api({ action: "review", college_code: u.college_code, status, note });
        const row = this.data.units.find(x => x.college_code === u.college_code);
        if (row) {
          row.state = d.submission.status;
          row.returned_note = d.submission.returned_note;
          row.reviewed_at = d.submission.reviewed_at;
        }
        const t = this.data.totals;
        ["submitted", "accepted", "returned"].forEach(k => (t[k] = this.data.units.filter(x => x.state === k).length));
        if (this.unitData && this.unit && this.unit.college_code === u.college_code) this.unitData.submission = d.submission;
        this.returnDialog = false;
        this.notify({ accepted: "ตรวจรับแล้ว", returned: "ส่งกลับให้สถานศึกษาแก้ไขแล้ว", reopen: "ยกเลิกการตรวจรับแล้ว" }[status]);
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.busy = false;
      }
    },
    openHistory(p) {
      this.historyPerson = p;
      this.historyChanged = false;
      this.historyDialog = true;
    },
    async saveSetting(key) {
      try {
        const d = await this.api({ action: "save_setting", key, value: this.settingEdit[key] });
        this.data.settings = d.settings;
        this.notify("บันทึกเกณฑ์แล้ว — กด “คำนวณใหม่” เพื่อตรวจคุณสมบัติด้วยเกณฑ์ใหม่");
      } catch (e) {
        this.notify(e.message, "error");
      }
    },
    writeXlsx(rows, sheet, file) {
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), sheet);
      XLSX.writeFile(wb, file);
    },
    exportUnits() {
      this.writeXlsx(
        this.filteredUnits.map((u, i) => ({
          ลำดับ: i + 1,
          รหัส: u.college_code,
          หน่วยงาน: u.college_name,
          จังหวัด: u.province_name,
          ภาค: u.region_name,
          บุคลากร: u.persons,
          "บันทึกประวัติแล้ว (คน)": u.recorded,
          "ยังไม่บันทึก (คน)": u.no_record,
          "ความครบถ้วน (%)": u.coverage,
          อ่านชั้นตราไม่ได้: u.unreadable,
          ครบเกณฑ์: u.eligible,
          ต้องตรวจสอบ: u.review,
          ข้อมูลไม่พอ: u.need_data,
          ยังไม่ครบเกณฑ์: u.not_yet,
          เสนอขอ: u.requests,
          สถานะ: STATE[u.state].label,
          วันที่ส่ง: u.submitted_at || "",
          เลขที่หนังสือ: u.doc_no || "",
          เหตุผลส่งกลับ: u.returned_note || ""
        })),
        "ติดตามการรายงาน",
        `เครื่องราชฯ_ติดตาม_${this.year}.xlsx`
      );
    },
    exportAudit() {
      this.writeXlsx(
        this.filteredIssues.map((i, n) => ({
          ลำดับ: n + 1,
          ระดับ: LEVEL[i.level].label,
          ปัญหา: i.label,
          รายละเอียด: i.detail,
          เลขบัตรประชาชน: i.id_card,
          "ชื่อ-สกุล": i.full_name,
          หน่วยงาน: i.college_name,
          รหัสหน่วยงาน: i.college_code || ""
        })),
        "ตรวจสอบประวัติ",
        `เครื่องราชฯ_ตรวจสอบประวัติ.xlsx`
      );
    },
    exportRequests() {
      this.writeXlsx(
        this.filteredRequests.map((r, i) => {
          const s = r.snapshot || {};
          return {
            ลำดับ: i + 1,
            หน่วยงาน: r.college_name,
            จังหวัด: r.province_name || "",
            ภาค: r.region_name || "",
            เลขบัตรประชาชน: r.id_card,
            "ชื่อ-สกุล": s.full_name,
            เพศ: s.sex || "",
            ประเภท: this.typeLabel(r.person_type),
            ตำแหน่ง: s.position_name,
            "ระดับ/วิทยฐานะ": s.rang || "",
            เงินเดือน: s.salary_s,
            วันเริ่มรับราชการ: thaiDate(s.start_date),
            ชั้นตราเดิม: r.last_class || "",
            วันที่ได้รับชั้นเดิม: thaiDate(r.last_class_date),
            ชั้นตราที่ขอ: r.request_class,
            ชื่อชั้นตรา: className(r.request_class),
            เกณฑ์: s.category_ref || "",
            เกษียณปีนี้: Number(s.retire_year) === Number(this.year) ? "ใช่" : "",
            ผลตรวจ: r.eligible_status === "review" ? "ต้องตรวจสอบ" : "ครบเกณฑ์",
            หมายเหตุ: r.note || "",
            สถานะหน่วยงาน: STATE[this.unitState(r.college_code)].label
          };
        }),
        "รายชื่อเสนอขอ",
        `เครื่องราชฯ_รายชื่อเสนอขอ_${this.year}.xlsx`
      );
    }
  }
};
</script>

<style scoped>
.kpi-box {
  height: 100%;
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
}
.kpi-value {
  font-size: 22px;
  font-weight: 700;
}
.reason {
  max-width: 300px;
  white-space: normal;
}
.dec-table >>> td {
  vertical-align: top;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}
</style>
